'use client';

import React, { useEffect, useRef, useState } from 'react';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import clsx from 'clsx';
import { useInView } from 'react-intersection-observer';

import styles from './styles.module.css';

import { AppBadges } from '@/components/AppBadge/AppBadges';
import { DownloadAppButton } from '@/components/DownloadAppButton';
import { DownloadAppQRCode } from '@/components/DownloadAppQRCode';

type UpsellBannerCta =
  | {
      variant?: 'button';
      label: string;
      trackingId: string;
      onClick?: () => void;
    }
  | {
      variant: 'badges';
      trackingId: string;
    };

type UpsellBannerProps = {
  headingId: string;
  title: string;
  description: string;
  logoSrc: string;
  logoClassName?: string;
  cardImages: readonly [string, string, string];
  cta: UpsellBannerCta;
  onView?: () => void;
};

export function UpsellBanner({
  headingId,
  title,
  description,
  logoSrc,
  logoClassName,
  cardImages,
  cta,
  onView,
}: UpsellBannerProps) {
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const hasFiredView = useRef(false);
  const autoPlayTimer = useRef<number | undefined>(undefined);

  const { ref: sectionRef, inView } = useInView({
    threshold: 0.9,
  });

  useEffect(
    () => () => {
      if (autoPlayTimer.current !== undefined) {
        window.clearTimeout(autoPlayTimer.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!inView) {
      return;
    }

    if (!hasFiredView.current) {
      hasFiredView.current = true;
      onView?.();
    }

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      return;
    }

    if (autoPlayTimer.current !== undefined) {
      window.clearTimeout(autoPlayTimer.current);
    }
    setIsAutoPlaying(true);
    autoPlayTimer.current = window.setTimeout(() => {
      setIsAutoPlaying(false);
      autoPlayTimer.current = undefined;
    }, 1500);
  }, [inView, onView]);

  return (
    <section
      ref={sectionRef}
      className={clsx(styles.container, {
        [styles.autoPlay]: isAutoPlaying,
      })}
      aria-labelledby={headingId}
    >
      <div className={styles.textContent}>
        <img
          src={logoSrc}
          alt=""
          aria-hidden
          className={clsx(styles.logo, logoClassName)}
        />
        <Heading as="h2" variant="xl" id={headingId} className={styles.title}>
          {title}
        </Heading>
        <Body variant="sm-regular" className={styles.description}>
          {description}
        </Body>
        <div className={styles.ctaWrapper}>
          {cta.variant === 'badges' ? (
            <AppBadges
              className={styles.appBadges}
              trackingId={cta.trackingId}
            />
          ) : (
            <DownloadAppButton
              trackingId={cta.trackingId}
              label={cta.label}
              onClick={cta.onClick}
            />
          )}
        </div>
        <div className={styles.qrCard}>
          <DownloadAppQRCode
            size="lg"
            withDepopLogo
            placement="outfits_upsell"
          />
        </div>
      </div>
      <div className={styles.cardStack} aria-hidden="true">
        <div className={`${styles.card} ${styles.card1}`}>
          <img src={cardImages[0]} alt="" />
        </div>
        <div className={`${styles.card} ${styles.card2}`}>
          <img src={cardImages[1]} alt="" />
        </div>
        <div className={`${styles.card} ${styles.card3}`}>
          <img src={cardImages[2]} alt="" />
        </div>
      </div>
    </section>
  );
}
